// In src/TabletContent.js

import React, { useState, useEffect } from 'react';
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from './firebase';
import { FaPlus, FaSave, FaTrash, FaEdit } from 'react-icons/fa';
const EditModeModal = ({ mode, onClose, onSave }) => {
  // Create a local copy of the mode to edit, so we don't change the main list until we save
  const [editedMode, setEditedMode] = useState(mode);

  const handleContentChange = (field, value) => {
    setEditedMode(prev => ({
      ...prev,
      content: { ...prev.content, [field]: value }
    }));
  };

  const handleAddVideoUrl = () => {
    const newUrl = prompt("Enter the new video URL:");
    if (newUrl && newUrl.startsWith('http')) {
      const newUrls = [...(editedMode.content?.videoUrls || []), newUrl];
      handleContentChange('videoUrls', newUrls);
    }
  };

  const handleRemoveVideoUrl = (indexToRemove) => {
    const newUrls = editedMode.content.videoUrls.filter((_, index) => index !== indexToRemove);
    handleContentChange('videoUrls', newUrls);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Edit Mode: {editedMode.id}</h3>
          <button onClick={onClose} className="modal-close-btn">&times;</button>
        </div>
        <div className="modal-body">
          <div className="detail-item"><strong>ID:</strong> {editedMode.id}</div>
          <div className="detail-item"><strong>Type:</strong> {editedMode.type}</div>
          
          {/* Special editor for TipMode */}
          {editedMode.id === 'TipMode' && (
            <div className="content-editor">
              <h4>Tip Mode Content</h4>
              <label htmlFor="videoUrl">Background Video URL</label>
              <input
                id="videoUrl"
                type="text"
                className="form-input"
                value={editedMode.content?.videoUrl || ''}
                onChange={(e) => handleContentChange('videoUrl', e.target.value)}
              />
            </div>
          )}

          {/* Special editor for AdsMode */}
          {editedMode.id === 'AdsMode' && (
            <div className="content-editor">
              <h4>Ad Video URLs</h4>
              <div className="url-list">
                {(editedMode.content?.videoUrls || []).map((url, index) => (
                  <div key={index} className="url-list-item">
                    <span className="url-text">{url}</span>
                    <button onClick={() => handleRemoveVideoUrl(index)} className="btn btn-decline btn-small">
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
              <button onClick={handleAddVideoUrl} className="btn btn-primary">
                <FaPlus /> Add Video URL
              </button>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button onClick={() => onSave(editedMode)} className="btn btn-approve">
            <FaSave /> Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};


function TabletContent() {
  const [modes, setModes] = useState([]);
  const [rules, setRules] = useState({ adFrequency: 15 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [editingMode, setEditingMode] = useState(null);

  // State for the "Add New Mode" form
  const [newModeId, setNewModeId] = useState('');
  const [newModeType, setNewModeType] = useState('');

  useEffect(() => {
    const docRef = doc(db, "tablet-content", "live-schedule");
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setModes(data.modes || []);
        setRules(data.schedulingRules || { adFrequency: 15 });
        setLoading(false);
      } else {
        setError("Live schedule document not found.");
        setLoading(false);
      }
    }, (err) => {
      console.error("Error fetching tablet content:", err);
      setError("Failed to load tablet content.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    try {
      const docRef = doc(db, "tablet-content", "live-schedule");
      await updateDoc(docRef, {
        modes: modes,
        schedulingRules: rules,
      });
      alert("Content schedule updated successfully!");
    } catch (err) {
      console.error("Error saving content:", err);
      setError("Failed to save content. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

 

  const handleAddMode = (e) => {
    e.preventDefault();
    if (!newModeId || !newModeType) {
      alert("Please provide both an ID and a Type for the new mode.");
      return;
    }
    const newMode = {
      id: newModeId,
      type: newModeType,
      enabled: true,
    };
    setModes([...modes, newMode]);
    setNewModeId('');
    setNewModeType('');
  };
   const handleSaveEditedMode = (updatedMode) => {
    const updatedModes = modes.map(mode => 
      mode.id === updatedMode.id ? updatedMode : mode
    );
    setModes(updatedModes);
    setEditingMode(null); // Close the modal
  };

  const handleToggleMode = (id) => {
    const updatedModes = modes.map(mode =>
      mode.id === id ? { ...mode, enabled: !mode.enabled } : mode
    );
    setModes(updatedModes);
  };

  const handleRemoveMode = (idToRemove) => {
    if (window.confirm(`Are you sure you want to remove the mode: ${idToRemove}?`)) {
      setModes(modes.filter(mode => mode.id !== idToRemove));
    }
  };

  if (loading) return <p className="loading-text">Loading tablet content...</p>;
  if (error) return <p className="error-text">{error}</p>;

  return (
    <div className="card">
        {editingMode && (
        <EditModeModal
          mode={editingMode}
          onClose={() => setEditingMode(null)}
          onSave={handleSaveEditedMode}
        />
      )}
      <div className="page-header-container">
        <div>
          <h2 className="page-header">Tablet Content Schedule</h2>
          <p className="page-subheader">Enable, disable, and add new modes for the tablets.</p>
        </div>
        <button onClick={handleSave} className="btn btn-approve" disabled={isSaving}>
          <FaSave />
          {isSaving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>

      {/* Scheduling Rules Section */}
      <div className="content-section">
        <h3 className="section-title">Global Scheduling Rules</h3>
        <div className="rule-item">
          <label htmlFor="adFrequency">Ad Frequency (Play ad after every X modes)</label>
          <input
            id="adFrequency"
            type="number"
            value={rules.adFrequency}
            onChange={(e) => setRules({ ...rules, adFrequency: Number(e.target.value) })}
            className="form-input"
          />
        </div>
      </div>

      {/* Modes List Section */}
      <div className="content-section">
        <h3 className="section-title">Mode Playlist</h3>
        <table className="table">
          <thead>
            <tr>
              <th>Mode ID</th>
              <th>Type</th>
              <th style={{ textAlign: 'center' }}>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {modes.map((mode, index) => (
              <tr key={index}>
                <td><strong>{mode.id}</strong></td>
                <td><span className="type-badge">{mode.type}</span></td>
                <td style={{ textAlign: 'center' }}>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={mode.enabled}
                      onChange={() => handleToggleMode(mode.id)}
                    />
                    <span className="slider round"></span>
                  </label>
                </td>
               <td className="actions-cell">
                   <button onClick={() => setEditingMode(mode)} className="btn btn-view btn-small">
                    <FaEdit /> Edit
                  </button>
                  <button onClick={() => handleRemoveMode(mode.id)} className="btn btn-decline btn-small">
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Add New Mode Section */}
      <div className="content-section">
        <h3 className="section-title">Add New Mode</h3>
        <form onSubmit={handleAddMode} className="add-mode-form">
          <input
            type="text"
            placeholder="Mode ID (e.g., NewGameMode)"
            value={newModeId}
            onChange={(e) => setNewModeId(e.target.value)}
            className="form-input"
          />
          <input
            type="text"
            placeholder="Type (e.g., Game, Trivia)"
            value={newModeType}
            onChange={(e) => setNewModeType(e.target.value)}
            className="form-input"
          />
          <button type="submit" className="btn btn-primary">
            <FaPlus /> Add Mode to Playlist
          </button>
        </form>
      </div>
    </div>
  );
}

export default TabletContent;